import { DefaultButton, Dialog, DialogFooter, DialogType, IDialogContentProps, PrimaryButton, Spinner, SpinnerSize, TextField } from "@fluentui/react";
import React from "react";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { useSelector } from "react-redux";
import { RootState } from "../../src/taskpane/components/_v2/redux/storeMain";
import { ComponentTypes } from "../../src/taskpane/config/config";
import authModule from "../../utilities/AuthModule";
import { FilePreCommitRequest, FolderPreCommitRequest, MetadataUpdateRequest, UpdateTriggerAction } from "../../src/taskpane/components/_v2/redux/api/local/api-local-interfaces";
import { SharePointFolderContentTypeIds, sharePointInternalFileName } from "../../src/taskpane/config/constants";
import { IDocUrlDropdown } from "../../src/taskpane/interfaces/IDocUrlDropdown";
import { logger } from "../../utilities/logger";
import { apiFilePreCommitCheck, apiFolderPreCommitCheck, apiUpdateItemMetadata } from "../../src/taskpane/services/ApiService";
import { FileFolderNameExtensions } from "../../src/taskpane/services/FileFolderNameExtensions";
import { resolveDestinationUrl } from "../../src/taskpane/components/helpers/urlDestination";

export interface ICMRenameModalProps {
  selectedItem: any;
  showDialog: boolean;
  closeDialog: () => void;
  closeOnSuccess: (item: any, updateValue: string) => void;
  isAdvancedSearch?: boolean;
  selectedDocumentLibraries?: IDocUrlDropdown[];
}

export const CMRenameModal = ({ selectedItem, showDialog, closeDialog, closeOnSuccess, isAdvancedSearch, selectedDocumentLibraries }: ICMRenameModalProps) => {
  const languageStrings = React.useContext(LanguageContext);

  const workingDirectories = useSelector((state: RootState) => state.workingDirectories?.workingDirectories);
  const currentDirectory = useSelector((state: RootState) => state.workingDirectories?.workingDirectories?.length > 0 ? state.workingDirectories?.workingDirectories[state.workingDirectories?.workingDirectories?.length - 1] : null);
  const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories?.oneDriveRootDetails);
  const tenantId = useSelector((state: RootState) => state.user?.account?.tenantId);
  const accountId = useSelector((state: RootState) => state.user?.account?.accountId);

  const isInOneDrive = React.useRef(currentDirectory?.type == ComponentTypes.OneDrive || currentDirectory?.type == ComponentTypes.OneDriveSubFolder);
  const originalFileName = React.useRef<string>();

  const [isBusy, setIsBusy] = React.useState<boolean>(false);
  const [input, setInput] = React.useState('');
  const [extention, setExtention] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState('');

  React.useEffect(() => {
    let fileName = '';

    if (selectedItem) {
      if (isAdvancedSearch) {
        fileName = selectedItem.name;
        if (!selectedItem.contenttypeid?.startsWith(SharePointFolderContentTypeIds.folder)) {
          const fileParts = fileName.split('.');
          if (fileParts.length > 1) {
            // setInput(fileParts.slice(0, -1).join('.'));
            fileName = fileParts.slice(0, -1).join('.');
            setExtention(fileParts[fileParts.length - 1]);
          }
        }
      }
      else {
        fileName = selectedItem.FileLeafRef;
        if (selectedItem.FSObjType === '0') {
          const fileParts = fileName.split('.');
          if (fileParts.length > 1) {
            // setInput(fileParts.slice(0, -1).join('.'));
            fileName = fileParts.slice(0, -1).join('.');
            setExtention(fileParts[fileParts.length - 1]);
          }
        }
      }
    }

    originalFileName.current = fileName?.trim().toLowerCase();
    setInput(fileName);
  }, [selectedItem]);


  const dialogContentProps: IDialogContentProps = {
    type: DialogType.normal,
    title: languageStrings.RenameOnly,
    showCloseButton: true,
    closeButtonAriaLabel: 'Close'
  };

  const validateName = React.useCallback(async (newValue: string): Promise<boolean> => {
    if (!newValue) {
      setErrorMessage(languageStrings.FieldRequired);
      return false;
    }

    let returnValue: boolean = false;

    try {
      const isFolder = selectedDocumentLibraries ? selectedItem.contenttypeid?.startsWith(SharePointFolderContentTypeIds.folder) : selectedItem.ContentTypeId?.startsWith(SharePointFolderContentTypeIds.folder)
      let siteUrl = '';
      let destinationUrl = '';
      let newFileName = `${newValue}${extention ? `.${extention}` : ''}`;

      if (selectedDocumentLibraries) {
        const selectedLibrary = selectedDocumentLibraries.find(x => x.driveId === selectedItem?.driveid);
        siteUrl = selectedLibrary.webUrl;
        destinationUrl = selectedItem.weburl.replace(`/${selectedItem.name}`, '');
      } else {
        siteUrl = isInOneDrive.current ? oneDriveRootDetails.sharePointSiteUrl : currentDirectory?.webUrl;

        destinationUrl = await resolveDestinationUrl(currentDirectory, { isOneDrive: isInOneDrive.current });
      }

      if (!isFolder) {
        const request: FilePreCommitRequest = {
          accessToken: isInOneDrive.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
          destinationUrl,
          siteUrl,
          fileName: newFileName,
          autoFolderCreation: false
        }

        const results = await apiFilePreCommitCheck(request);

        if (results.exists) {
          setErrorMessage(languageStrings.FileSameName);
        }
        else if (results.checkedOut) {
          setErrorMessage(languageStrings.FileSameCheckedOut);
        }
        else if (results.locked) {
          setErrorMessage(languageStrings.FileSameLocked);
        }
        else {
          setErrorMessage('');
          returnValue = true;
        }
      } else {
        const folderRequest: FolderPreCommitRequest = {
          accessToken: isInOneDrive.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
          destinationUrl,
          siteUrl,
          folderName: newFileName
        }

        const results = await apiFolderPreCommitCheck(folderRequest);
        if (results.exists) {
          setErrorMessage(languageStrings.FolderSameName);
        }
        else {
          setErrorMessage('');
          returnValue = true;
        }
      }
    } catch (error) {
      logger(error, "CMRenameModal.tsx (validateName)");
    }

    return returnValue;
  }, [extention, selectedDocumentLibraries, selectedItem, setErrorMessage]);

  const onUpdate = React.useCallback(async () => {

    setIsBusy(true);
    let isValidName: boolean;
    const sanitizedName: string = FileFolderNameExtensions.EnsureValidName(input);
    try {
      isValidName = await validateName(sanitizedName);
      if (isValidName) {
        let siteUrl = '';
        let listId = '';

        if (selectedDocumentLibraries) {
          const selectedLibrary = selectedDocumentLibraries.find(x => x.driveId === selectedItem?.driveid);
          siteUrl = selectedLibrary.webUrl;
          listId = selectedLibrary.sharePointListId;
        } else {
          siteUrl = isInOneDrive.current ? oneDriveRootDetails.sharePointSiteUrl : currentDirectory?.webUrl;
          listId = isInOneDrive?.current ? oneDriveRootDetails?.sharePointListId : currentDirectory.sharePointListId;
        }

        let request: MetadataUpdateRequest = {
          accessToken: isInOneDrive.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
          tenantGuid: tenantId,
          accountId,
          siteUrl,
          listId,
          teamId: workingDirectories?.find(dir => dir.teamGroupId)?.teamGroupId,
          channelId: workingDirectories?.find(dir => dir.teamChannelId)?.teamChannelId,
          listItemUniqueId: selectedItem.uniqueid ?? selectedItem.UniqueId.replace('{', '').replace('}', ''),
          isFolder: selectedItem.contenttypeid ? selectedItem.contenttypeid?.startsWith(SharePointFolderContentTypeIds.folder) : selectedItem?.ContentTypeId?.startsWith(SharePointFolderContentTypeIds.folder),
          isListItem: false,
          listSettings: {
            enableMinorVersions: false,
          },
          triggeredByAction: UpdateTriggerAction.Rename,
          metadata: [{
            type: 'Text',
            internalName: sharePointInternalFileName,
            value: sanitizedName
          }]
        }

        await apiUpdateItemMetadata(request);
      }

    } catch (error) {
      logger(error, "CMRenameModal.tsx (onUpdate)");
    }

    setIsBusy(false);
    isValidName && closeOnSuccess(selectedItem, `${sanitizedName}${extention ? `.${extention}` : ''}`);

  }, [selectedDocumentLibraries, selectedItem, input, validateName, apiUpdateItemMetadata, closeOnSuccess]);

  const onChange = React.useCallback(async (event?: React.ChangeEvent<HTMLInputElement>, newValue?: string) => {
    if (errorMessage) {
      setErrorMessage("");
    }
    setInput(newValue?.trim());
  }, [setErrorMessage, setInput, errorMessage]);

  const handleKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onUpdate();
    }
  }, [onUpdate]);

  return (
    <Dialog hidden={!showDialog} onDismiss={() => closeDialog()} dialogContentProps={dialogContentProps}>
      <TextField
        autoFocus
        required
        defaultValue={input}
        onChange={onChange}
        onKeyDown={handleKeyDown}
        errorMessage={errorMessage}
      />
      <DialogFooter>
        <PrimaryButton
          disabled={isBusy || errorMessage.length > 0 || input?.length === 0 || originalFileName.current === input?.toLowerCase()}
          text={languageStrings.RenameOnly}
          onClick={onUpdate}>
          {isBusy && <Spinner size={SpinnerSize.small} />}
        </PrimaryButton>
        <DefaultButton onClick={closeDialog} text={languageStrings.Cancel} disabled={isBusy} /></DialogFooter>
    </Dialog>
  )
};
